import log from 'loglevel';
import { ConfigurationManager } from '../configuration-manager/configuration-manager';

interface CookieOptions {
  domain?: string,
  path?: string,
  maxAge?: string | null,
  expires?: string | null,
  secure?: boolean
}

export class CookieManager {

  private static farPast = 'Thu, 01 Jan 1970 00:00:00 GMT'; 

  private static inFifteenMonths(): string {
    let fifteenMonthsInMs = 3.942e+10;
    return new Date(Date.now() + fifteenMonthsInMs).toUTCString()
  }

  private static getDefaultOptions(): CookieOptions {
    return {
      domain: this.getRootDomain() || '',
      path: '/',
      maxAge: null,
      expires: this.inFifteenMonths(),
      secure: location && location.protocol === 'https:'
    };
  }

  /**
   * Sets a cookie with default or custom options.
   * @param name {string} The name of the cookie
   * @param value {string} The value of the cookie
   * @param options {CookieOptions} Optional options for the cookie
   */
  static setCookie(name: string, value: string, options?: CookieOptions): void {
    options = Object.assign(this.getDefaultOptions(), options);
    let cookieValue = 
      `${encodeURIComponent(name)}=${encodeURIComponent(value)}` +
      `;domain=${options.domain}` +
      `;path=${options.path}` + 
      (options.maxAge ? `;max-age=${options.maxAge}` : '') +
      (options.expires ? `;expires=${options.expires}` : '') +
      (options.secure ? `;secure` : '') + 
      ';sameSite=Strict';
    log.debug(`Set cookie: ${cookieValue}`);
    document.cookie = cookieValue;
  }

  /**
   * Returns the value of a cookie.
   * @param name {string} The name of the cookie
   */
  static getCookie(name: string): string | undefined {
    const cookie = document.cookie
    .split('; ')
    .find((row: string) => row.startsWith(encodeURIComponent(name)))
    return cookie ? cookie.split('=')[1] : undefined;
  }

  /**
   * Deletes a cookie by setting the expiration time in the past.
   * @param name {string} The name of the cookie
   */
  static deleteCookie(name: string): void {
    this.setCookie(name, '', {
      expires: this.farPast
    });
    // also delete cookie from current domain if it is not root domain.
    if (ConfigurationManager.get('rootDomain')) {
      this.setCookie(name, '', {
        expires: this.farPast,
        domain: ''
      });
    }
  }

  /**
   * Converts the old `xng.gid` and `xng.sid` cookies into the current cookie storage format.
   */
  static convertLegacyCookies(): void {
    const xngGidCookie = this.getCookie('xng.gid');
    if (xngGidCookie) {
      log.debug('Converting legacy xng.gid cookie. This should only happen once.');
      ConfigurationManager.applyConfiguration({globalUserId: xngGidCookie});
      this.deleteCookie('xng.gid');
    }

    const xngSidCookie = this.getCookie('xng.sid');
    if (xngSidCookie) {
      log.debug('Converting legacy xng.sid cookie. This should only happen once.');
      ConfigurationManager.applyConfiguration({sessionId: xngSidCookie});
      this.deleteCookie('xng.sid');
    }

    const trackingOptOutCookie = this.getCookie('xng.tracking-opt-out');
    if (trackingOptOutCookie) {
      log.debug('Converting legacy xng.tracking-opt-out cookie. This should only happen once.');
      ConfigurationManager.applyConfiguration({trackingOptIn: trackingOptOutCookie === 'true' ? false : true });
      this.deleteCookie('xng.tracking-opt-out');
    }
  }

  /**
  * Returns the manually set or automatically retrieved root domain if `rootDomain` configuration is set
  * with a string or boolean.
  */
  private static getRootDomain(): string | undefined {
    const rootDomainValue = ConfigurationManager.get('rootDomain');
    if (rootDomainValue) {
      if (typeof rootDomainValue === 'string') {
        // TODO: investigate if the leading dot really makes sense, found contradicting info so far
        return rootDomainValue;
      } else {
        // automatically parses the root domain.
        // DOES NOT WORK WITH SECOND LEVEL DOMAINS! For these the root domain must be passed manually.
        return location.hostname.split('.').reverse().splice(0, 2).reverse().join('.');
      }
    }
    return undefined;
  }
}
